import React, { useState } from 'react';
import { Button, Form, Spinner, Modal } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { useAuthState } from '../../Context';
import { portalLogin, submitInitialEmail } from '../../Context/actions/authActions';
import { isSupportedBrowser } from '../../utils/helper';
import { isUniversalLoginHost, rememberPortal } from '../../config';
import './PortalLogin.scss';

const PortalLogin = () => {
    const { register, handleSubmit } = useForm();
    const [loading, setLoadingState] = useState(false);
    const [portalList, setPortalList] = useState([]);
    const [showPortalSelect, setShowPortalSelect] = useState(false);
    const [currentStep, setCurrentStep] = useState('emailAddress'); // 'emailAddress', 'portal', 'password'
    const [emailAddress, setEmailAddress] = useState('');
    const [selectedPortal, setSelectedPortal] = useState(null);
    const {
        authDispatch,
        authUser: { errorMessage },
    } = useAuthState();

    const setAuthError = (error) => {
        authDispatch({ type: 'LOGIN_ERROR', error });
    };

    const handlePortalLogin = (portal, token = '') => {
        // Always complete login on the current host — never bounce to another domain. On a
        // On a universal host, move to the selected portal's own domain; elsewhere stay put.
        const onUniversalHost = isUniversalLoginHost();
        if (onUniversalHost) {
            // Lets a relaunch hand off to the owning origin instead of re-deriving it from the email.
            rememberPortal(portal);
        }
        const targetHost = onUniversalHost ? portal.hostname : window.location.host;
        const url = new URL(`${window.location.protocol}//${targetHost}/sso`);
        url.searchParams.append('token', token);
        url.searchParams.append('modelTypeID', portal.modelTypeID);
        url.searchParams.append('entityID', portal.entityID);
        window.location.href = url.toString();
    };

    const handleEmailAddressSubmit = async (data) => {
        const { emailAddress } = data;
        setEmailAddress(emailAddress);
        setLoadingState(true);

        try {
            const response = await submitInitialEmail(authDispatch, emailAddress);
            if (response?.portals?.length > 0) {
                setPortalList(response.portals);
                if (response.portals.length > 1) {
                    setCurrentStep('portal');
                    setShowPortalSelect(true);
                } else if (response.portals.length === 1) {
                    setSelectedPortal(response.portals[0]);
                    setCurrentStep('password');
                }
            } else {
                setAuthError('No accounts found for this email');
            }
        } catch (error) {
            setAuthError('There was an issue retrieving your account. Please try again.');
        }

        setLoadingState(false);
    };

    const handlePortalSelect = (portal) => {
        setSelectedPortal(portal);
        setShowPortalSelect(false);
        setCurrentStep('password');
    };

    const handlePasswordSubmit = async (data) => {
        const { password } = data;
        setLoadingState(true);

        try {
            let response = await portalLogin(authDispatch, {
                email: emailAddress,
                password,
                modelTypeID: selectedPortal?.modelTypeID,
                entityID: selectedPortal?.entityID,
            });

            if (response) {
                if (!selectedPortal?.hostname) {
                    setAuthError('There was an issue with the selected company. Please contact your administrator.');
                    return;
                }

                handlePortalLogin(selectedPortal, response.token);
            }
        } catch (error) {
            console.log('Login error:', error);
        } finally {
            setLoadingState(false);
        }
    };

    const handleBack = () => {
        setAuthError(null);
        if (currentStep === 'password') {
            if (portalList.length > 1) {
                setCurrentStep('portal');
                setShowPortalSelect(true);
            } else {
                setCurrentStep('emailAddress');
            }
        } else if (currentStep === 'portal') {
            setCurrentStep('emailAddress');
            setShowPortalSelect(false);
        }
    };

    if (!isSupportedBrowser()) {
        return (
            <div className="portal-login-wrapper">
                <div className="login-card shadow text-center">
                    <h3 className="mb-3">Browser Not Supported</h3>
                    <p className="text-muted">
                        The browser you are using is not supported by this application. Please use one of the following
                        browsers to continue:
                    </p>
                    <ul className="supported-browser-list text-start">
                        <li>Google Chrome</li>
                        <li>Apple Safari</li>
                        <li>Mozilla Firefox</li>
                        <li>Microsoft Edge</li>
                        <li>Opera</li>
                    </ul>
                </div>
            </div>
        );
    }

    return (
        <>
            {!showPortalSelect ? (
                <div className="portal-login-wrapper">
                    <div className="login-card shadow">
                        <h3 className="text-center mb-4">
                            {currentStep === 'emailAddress'
                                ? 'Sign in to your account'
                                : currentStep === 'password'
                                ? `Welcome${selectedPortal ? ` to ${selectedPortal.name}` : ''}`
                                : 'Sign in to your account'}
                        </h3>

                        {currentStep === 'emailAddress' && (
                            <Form onSubmit={handleSubmit(handleEmailAddressSubmit)}>
                                <Form.Group className="form-floating mb-3">
                                    <Form.Label>Email address</Form.Label>
                                    <Form.Control
                                        type="email"
                                        placeholder="name@example.com"
                                        {...register('emailAddress', { required: 'Email is required' })}
                                        isInvalid={errorMessage}
                                        autoComplete="username"
                                        onChange={() => setAuthError(null)}
                                    />
                                </Form.Group>

                                {errorMessage && <div className="text-danger text-end mb-2 small">{errorMessage}</div>}

                                <div className="sign-up-button">
                                    <Button
                                        type="submit"
                                        variant="info"
                                        className="custom-login-btn"
                                        disabled={loading}
                                    >
                                        {loading ? <Spinner animation="border" size="sm" /> : 'Continue'}
                                    </Button>
                                </div>
                            </Form>
                        )}

                        {currentStep === 'password' && (
                            <Form onSubmit={handleSubmit(handlePasswordSubmit)}>
                                <div className="mb-3">
                                    <small className="text-muted">Email: {emailAddress}</small>
                                    {selectedPortal && (
                                        <div>
                                            <small className="text-muted">Company: {selectedPortal.name}</small>
                                        </div>
                                    )}
                                </div>

                                <Form.Group className="form-floating mb-3">
                                    <Form.Label>Password</Form.Label>
                                    <Form.Control
                                        type="password"
                                        placeholder="Password"
                                        {...register('password', { required: 'Password is required' })}
                                        isInvalid={errorMessage}
                                        autoComplete="current-password"
                                        onChange={() => setAuthError(null)}
                                        autoFocus
                                    />
                                </Form.Group>

                                {errorMessage && <div className="text-danger text-end mb-2 small">{errorMessage}</div>}

                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <Link
                                        to={{
                                            pathname: '/forgot-password',
                                            // Carry the entity the driver selected so the reset is
                                            // scoped to the correct company/PC/enterprise — a driver
                                            // can exist under several of them with separate passwords.
                                            state: selectedPortal
                                                ? {
                                                      modelTypeID: selectedPortal.modelTypeID,
                                                      entityID: selectedPortal.entityID,
                                                  }
                                                : undefined,
                                        }}
                                        className="small"
                                    >
                                        Forgot Password?
                                    </Link>
                                </div>

                                <div className="d-flex flex-column">
                                    <Button type="submit" variant="success" className="mb-2" disabled={loading}>
                                        {loading ? <Spinner animation="border" size="sm" /> : 'Sign In'}
                                    </Button>
                                    <Button onClick={handleBack} variant="info" className="custom-login-btn w-100">
                                        Back
                                    </Button>
                                </div>
                            </Form>
                        )}
                    </div>
                </div>
            ) : (
                <Modal show centered>
                    <Modal.Header>
                        <Modal.Title>Select a Company</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <div className="mb-3">
                            <small className="text-muted">Email: {emailAddress}</small>
                        </div>
                        <div className="portal-list">
                            {portalList.map((portal, index) => (
                                <div key={index} className="portal-item" onClick={() => handlePortalSelect(portal)}>
                                    <span className="portal-name">{portal.name}</span>
                                    <i className="fas fa-chevron-right"></i>
                                </div>
                            ))}
                        </div>
                        <div className="mt-3">
                            <Button onClick={handleBack} variant="info" className="custom-login-btn w-100">
                                Back
                            </Button>
                        </div>
                    </Modal.Body>
                </Modal>
            )}
        </>
    );
};

export default PortalLogin;
